(() => {
  'use strict';

  const SELECTORS = {
    container: '[data-subscription-buy-box]',
    json: '[data-subscription-buy-box-json]',
    sellingPlanInput: '[data-selling-plan-input]',
    row: '.subscription-buy-box__row',
    radio: '[data-purchase-type-radio]',
    frequencySelect: '[data-frequency-select]',
    frequencyWrapper: '.subscription-buy-box__frequency',
    frequencyToggle: '[data-frequency-toggle]',
    frequencyToggleText: '[data-frequency-toggle-text]',
    frequencyMenu: '[data-frequency-menu]',
    frequencyOption: '[data-frequency-option]',
    subscriptionExtras: '[data-subscription-extras]',
    subscriptionBlock: '[data-subscription-block]',
    giftBlock: '[data-subscription-gift]',
    giftBullet: '[data-gift-bullet]',
    productInfo: 'product-info',
    variantIdInput: 'input[name="id"]',
    fallbackVariantIdInput: 'input[form*="product-form"][name="id"]',
    subPriceDisplay: '[data-price-display][data-option="subscription"]',
    oneTimePriceDisplay: '[data-price-display][data-option="one-time"]',
    subTitle: '[data-subscription-title]',
    subSavings: '[data-subscription-savings]',
    oneTimeSavings: '[data-one-time-savings]',
    costPerServing: '[data-cost-per-serving]',
    costPerServingRow: '[data-cost-per-serving-row]',
    currentPrice: '.subscription-buy-box__current',
    comparePrice: '.subscription-buy-box__compare',
  };

  const CLASSES = {
    initialized: 'data-subscription-buy-box-initialized',
    compareFirst: 'subscription-buy-box__price--compare-first',
    oneTimeSelectedRow: 'subscription-buy-box__row--selected',
    subscriptionSelectedBlock: 'subscription-buy-box__subscription-block--selected',
  };

  const PURCHASE_TYPE = {
    oneTime: 'one-time',
    subscription: 'subscription',
  };

  const toStringId = (value) => (value == null ? '' : String(value));

  const parseJson = (text) => {
    try {
      return JSON.parse(text);
    } catch (_error) {
      return null;
    }
  };

  const formatMoney = (cents) => (typeof cents === 'number' ? (cents / 100).toFixed(2) : '');

  const getDiscountPct = (compareCents, priceCents) =>
    compareCents > 0 && compareCents > priceCents
      ? Math.floor(
          (((compareCents - priceCents) * 100 * 2 + compareCents) / (compareCents * 2))
        )
      : 0;

  const getCheckedPurchaseType = (container) =>
    container.querySelector(`${SELECTORS.radio}:checked`)?.value ?? null;

  const getVariantData = (variants, variantId) => variants[toStringId(variantId)] ?? null;

  const getFirstSellingPlan = (variant) => variant?.selling_plans?.[0] ?? null;

  const syncProductButtonPrice = (container) => {
    const productInfo = container.closest(SELECTORS.productInfo);
    if (typeof productInfo?.updateButtonPrice === 'function') {
      productInfo.updateButtonPrice();
      return;
    }

    customElements.whenDefined('product-info').then(() => {
      if (typeof productInfo?.updateButtonPrice === 'function') {
        productInfo.updateButtonPrice();
      }
    });
  };

  const getVariantIdInput = (container, sellingPlanInput) => {
    const productInfo = container.closest(SELECTORS.productInfo);
    const scopedVariantIdInput = productInfo?.querySelector(SELECTORS.variantIdInput);
    if (scopedVariantIdInput) return scopedVariantIdInput;

    const linkedFormId = sellingPlanInput.getAttribute('form');
    if (linkedFormId) {
      const linkedForm = document.getElementById(linkedFormId);
      const linkedVariantIdInput = linkedForm?.querySelector(SELECTORS.variantIdInput);
      if (linkedVariantIdInput) return linkedVariantIdInput;
    }

    return document.querySelector(SELECTORS.fallbackVariantIdInput);
  };

  const ensureCompareElement = (priceElement, currentElement) => {
    const existing = priceElement.querySelector(SELECTORS.comparePrice);
    if (existing) return existing;

    const compareElement = document.createElement('span');
    compareElement.className = SELECTORS.comparePrice.replace('.', '');
    const optionType = priceElement.getAttribute('data-option');
    const showCompareFirst =
      optionType === PURCHASE_TYPE.oneTime || priceElement.classList.contains(CLASSES.compareFirst);

    if (showCompareFirst && currentElement) {
      priceElement.insertBefore(compareElement, currentElement);
    } else {
      priceElement.appendChild(compareElement);
    }

    return compareElement;
  };

  const setPriceDisplay = (priceElement, { formattedPrice, formattedCompare, showCompare }) => {
    if (!priceElement) return;

    const currentElement = priceElement.querySelector(SELECTORS.currentPrice);
    if (currentElement) currentElement.textContent = formattedPrice ?? '';

    const shouldShowCompare = Boolean(formattedCompare && showCompare);
    const compareElement = shouldShowCompare
      ? ensureCompareElement(priceElement, currentElement)
      : priceElement.querySelector(SELECTORS.comparePrice);

    if (!compareElement) return;

    compareElement.textContent = shouldShowCompare ? formattedCompare : '';
    compareElement.style.display = shouldShowCompare ? '' : 'none';
  };

  const getPricingState = (variant, subPlan) => {
    const variantCompareOrBase = variant.formatted_compare_at_price || variant.formatted_price;
    const variantCompareCents = variant.compare_at_price || variant.price;

    const subscriptionPrice = subPlan?.formatted_price || formatMoney(variant.price);
    const subscriptionShowCompare = subPlan
      ? variantCompareCents > subPlan.price
      : variant.compare_at_price > variant.price;

    return {
      variantCompareCents,
      subscription: {
        formattedPrice: subPlan ? subscriptionPrice : variant.formatted_price || formatMoney(variant.price),
        formattedCompare: variantCompareOrBase,
        showCompare: subscriptionShowCompare,
      },
      oneTime: {
        formattedPrice: variant.formatted_price || formatMoney(variant.price),
        formattedCompare: variantCompareOrBase,
        showCompare: variant.compare_at_price > variant.price,
      },
    };
  };

  const closeFrequencyMenu = (toggleElement, menuElement) => {
    if (!toggleElement || !menuElement) return;
    toggleElement.setAttribute('aria-expanded', 'false');
    menuElement.hidden = true;
  };

  const setFrequencySelection = (selectElement, toggleTextElement, menuElement, planId) => {
    if (!selectElement) return;

    const normalizedPlanId = toStringId(planId);
    const selectedOption = [...selectElement.options].find(
      (option) => option.value === normalizedPlanId
    );

    if (selectedOption) selectElement.value = selectedOption.value;
    if (toggleTextElement) {
      toggleTextElement.textContent = selectedOption?.textContent || selectElement.options[0]?.textContent || '';
    }

    menuElement?.querySelectorAll(SELECTORS.frequencyOption).forEach((option) => {
      const isSelected = option.value === selectElement.value;
      option.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    });
  };

  const renderSellingPlans = (selectElement, plans = [], frequencyControls = {}) => {
    if (!selectElement) return;

    const { wrapper, toggleText, menu } = frequencyControls;
    const shouldShowControls = plans.length > 1;

    if (!plans.length) {
      if (wrapper) wrapper.hidden = true;
      return;
    }

    const options = plans.map((plan) => {
      const option = document.createElement('option');
      option.value = toStringId(plan.id);
      option.textContent = plan.name || `Plan ${plan.id}`;
      return option;
    });

    selectElement.replaceChildren(...options);
    if (wrapper) wrapper.hidden = !shouldShowControls;

    const firstPlan = plans[0];
    if (firstPlan) {
      selectElement.value = toStringId(firstPlan.id);
    }

    if (menu) {
      menu.replaceChildren(
        ...plans.map((plan, index) => {
          const item = document.createElement('li');
          const option = document.createElement('button');
          option.type = 'button';
          option.className = 'subscription-buy-box__frequency-option';
          option.value = toStringId(plan.id);
          option.textContent = plan.name || `Plan ${plan.id}`;
          option.setAttribute('data-frequency-option', '');
          if (index === 0) {
            option.classList.add('is-selected');
            option.setAttribute('aria-current', 'true');
          }
          item.appendChild(option);
          return item;
        })
      );
    }

    setFrequencySelection(selectElement, toggleText, menu, firstPlan?.id);
  };

  // MODIFIED: now accepts badgeText to override auto-calculated discount
  const setSavingsElement = (savingsElement, discountPct, badgeText) => {
    if (!savingsElement) return;
    if (badgeText) {
      savingsElement.textContent = badgeText;
      savingsElement.classList.remove('is-hidden');
    } else {
      savingsElement.textContent = discountPct > 0 ? `${discountPct}% Off` : '';
      savingsElement.classList.toggle('is-hidden', discountPct <= 0);
    }
  };

  const setCostPerServing = (costPerServingElement, variant) => {
    if (!costPerServingElement) return;
    const formattedCost = variant?.formatted_cost_per_serving || '';
    const costPerServingRow = costPerServingElement.closest(SELECTORS.costPerServingRow);

    costPerServingElement.textContent = formattedCost;
    if (costPerServingRow) costPerServingRow.hidden = !formattedCost;
  };

  // MODIFIED: now accepts badgeText
  const setSubscriptionTitle = (
    titleElement,
    savingsElement,
    subscriptionLabel,
    subPlan,
    variantCompareCents,
    badgeText
  ) => {
    if (!titleElement || !subscriptionLabel) return;

    titleElement.textContent = subscriptionLabel;
    setSavingsElement(
      savingsElement,
      subPlan ? getDiscountPct(variantCompareCents, subPlan.price) : 0,
      badgeText
    );
  };

  const syncSelectionClasses = ({ rows, radios, subscriptionBlock, sellingPlanInput }) => {
    const isSubscription = Boolean(sellingPlanInput.value);

    rows.forEach((row) => {
      const isOneTime = row.getAttribute('data-option') === PURCHASE_TYPE.oneTime;
      row.classList.toggle(CLASSES.oneTimeSelectedRow, isOneTime && !isSubscription);
    });

    radios.forEach((radio) => {
      radio.checked = (radio.value === PURCHASE_TYPE.subscription) === isSubscription;
    });

    subscriptionBlock?.classList.toggle(CLASSES.subscriptionSelectedBlock, isSubscription);
  };

  const setSellingPlanValue = (sellingPlanInput, frequencySelect, planId) => {
    const normalizedPlanId = planId ? toStringId(planId) : '';
    let appliedPlanId = normalizedPlanId;

    if (normalizedPlanId && frequencySelect) {
      const hasMatchingOption = [...frequencySelect.options].some(
        (option) => option.value === normalizedPlanId
      );

      if (hasMatchingOption) {
        frequencySelect.value = normalizedPlanId;
      } else {
        const fallbackPlanId = frequencySelect.options[0]?.value || '';
        frequencySelect.value = fallbackPlanId;
        appliedPlanId = fallbackPlanId;
      }
    }

    sellingPlanInput.value = appliedPlanId;
  };

  const initContainer = (container) => {
    if (!container || container.hasAttribute(CLASSES.initialized)) return;
    container.setAttribute(CLASSES.initialized, 'true');

    const jsonElement = container.querySelector(SELECTORS.json);
    const sellingPlanInput = container.querySelector(SELECTORS.sellingPlanInput);
    if (!jsonElement || !sellingPlanInput) return;

    const data = parseJson(jsonElement.textContent);
    if (!data) return;

    const variants = data.variants ?? {};
    const defaultToSubscription = data.defaultToSubscription !== false;
    const subscriptionLabel = data.subscriptionLabel || 'Subscribe & Save';
    const sectionId = container.getAttribute('data-section-id');

    // ADDED: read badge JSON from data attribute on the container
    const badgeJsonAttr = container.getAttribute('data-badge-json');
    const badgeJson = badgeJsonAttr ? parseJson(badgeJsonAttr) : null;

    const rows = [...container.querySelectorAll(SELECTORS.row)];
    const radios = [...container.querySelectorAll(SELECTORS.radio)];
    const frequencySelect = container.querySelector(SELECTORS.frequencySelect);
    const frequencyWrapper = container.querySelector(SELECTORS.frequencyWrapper);
    const frequencyToggle = container.querySelector(SELECTORS.frequencyToggle);
    const frequencyToggleText = container.querySelector(SELECTORS.frequencyToggleText);
    const frequencyMenu = container.querySelector(SELECTORS.frequencyMenu);
    const subscriptionExtras = container.querySelector(SELECTORS.subscriptionExtras);
    const subscriptionBlock = container.querySelector(SELECTORS.subscriptionBlock);
    const giftBlock = container.querySelector(SELECTORS.giftBlock);
    const giftBullet = container.querySelector(SELECTORS.giftBullet);
    const subscriptionPriceDisplay = container.querySelector(SELECTORS.subPriceDisplay);
    const oneTimePriceDisplay = container.querySelector(SELECTORS.oneTimePriceDisplay);
    const subscriptionTitleElement = container.querySelector(SELECTORS.subTitle);
    const subscriptionSavingsElement = container.querySelector(SELECTORS.subSavings);
    const oneTimeSavingsElement = container.querySelector(SELECTORS.oneTimeSavings);
    const costPerServingElement = container.querySelector(SELECTORS.costPerServing);

    const applyCommonUiState = () => {
      syncSelectionClasses({ rows, radios, subscriptionBlock, sellingPlanInput });
      syncProductButtonPrice(container);
    };

    const updateUiForVariant = (variantId) => {
      const variant = getVariantData(variants, variantId);
      if (!variant) return;

      const subPlan = getFirstSellingPlan(variant);
      const pricing = getPricingState(variant, subPlan);

      // ADDED: get badge text for this variant's box count
      const variantTitle = variant.title || '';
      const boxCount = variantTitle.split(' ')[0];
      const badgeText = badgeJson ? (badgeJson[boxCount] || null) : null;

      setPriceDisplay(subscriptionPriceDisplay, pricing.subscription);
      setPriceDisplay(oneTimePriceDisplay, pricing.oneTime);
      setCostPerServing(costPerServingElement, variant);

      if (giftBlock) {
        const minServings = Number(giftBlock.getAttribute('data-gift-min-servings')) || 0;
        giftBlock.hidden = (Number(variant.serving_size) || 0) < minServings;
      }
      if (giftBullet) {
        const minServings = Number(giftBullet.getAttribute('data-gift-min-servings')) || 0;
        const servings = Number(variant.serving_size) || 0;
        const isNoGift = servings < minServings;
        giftBullet.textContent = isNoGift
          ? giftBullet.getAttribute('data-gift-text-no')
          : giftBullet.getAttribute('data-gift-text-yes');
        giftBullet.classList.toggle('subscription-buy-box__gift-bullet--no', isNoGift);
      }

      // MODIFIED: pass badgeText
      setSubscriptionTitle(
        subscriptionTitleElement,
        subscriptionSavingsElement,
        subscriptionLabel,
        subPlan,
        pricing.variantCompareCents,
        badgeText
      );
      setSavingsElement(
        oneTimeSavingsElement,
        getDiscountPct(pricing.variantCompareCents, variant.price)
      );
      renderSellingPlans(frequencySelect, variant.selling_plans || [], {
        wrapper: frequencyWrapper,
        toggleText: frequencyToggleText,
        menu: frequencyMenu,
      });

      const checkedType = getCheckedPurchaseType(container);
      const wantsSubscription = checkedType
        ? checkedType === PURCHASE_TYPE.subscription
        : defaultToSubscription;

      if (wantsSubscription && subPlan) {
        setSellingPlanValue(sellingPlanInput, frequencySelect, subPlan.id);
      } else {
        setSellingPlanValue(sellingPlanInput, frequencySelect, null);
      }

      applyCommonUiState();
    };

    const onPurchaseTypeChange = () => {
      const checkedType = getCheckedPurchaseType(container);
      const variantIdInput = getVariantIdInput(container, sellingPlanInput);
      const variant = getVariantData(variants, variantIdInput?.value);
      const subPlan = getFirstSellingPlan(variant);
      const shouldUseSubscriptionPlan =
        checkedType === PURCHASE_TYPE.subscription && Boolean(subPlan);
      const selectedPlanId = frequencySelect?.value || subPlan?.id;

      setSellingPlanValue(
        sellingPlanInput,
        frequencySelect,
        shouldUseSubscriptionPlan ? selectedPlanId : null
      );
      applyCommonUiState();
    };

    frequencySelect?.addEventListener('change', (event) => {
      const checkedType = getCheckedPurchaseType(container);
      if (checkedType !== PURCHASE_TYPE.subscription) return;

      setSellingPlanValue(sellingPlanInput, null, event.target.value || null);
      setFrequencySelection(
        frequencySelect,
        frequencyToggleText,
        frequencyMenu,
        event.target.value || null
      );
      syncProductButtonPrice(container);
    });

    frequencyToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      const isOpen = frequencyToggle.getAttribute('aria-expanded') === 'true';
      frequencyToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      if (frequencyMenu) frequencyMenu.hidden = isOpen;
    });

    frequencyMenu?.addEventListener('click', (event) => {
      const option = event.target.closest(SELECTORS.frequencyOption);
      if (!option) return;

      event.preventDefault();
      setFrequencySelection(frequencySelect, frequencyToggleText, frequencyMenu, option.value);
      frequencySelect?.dispatchEvent(new Event('change', { bubbles: true }));
      closeFrequencyMenu(frequencyToggle, frequencyMenu);
    });

    document.addEventListener('click', (event) => {
      if (!frequencyWrapper?.contains(event.target)) {
        closeFrequencyMenu(frequencyToggle, frequencyMenu);
      }
    });

    frequencyWrapper?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeFrequencyMenu(frequencyToggle, frequencyMenu);
      frequencyToggle?.focus();
    });

    radios.forEach((radio) => {
      radio.addEventListener('change', onPurchaseTypeChange);
    });

    const selectPurchaseType = (type) => {
      const radio = radios.find((candidate) => candidate.value === type);
      if (!radio) return;

      radio.checked = true;
      onPurchaseTypeChange();
    };

    rows.forEach((row) => {
      row.addEventListener('click', () => {
        const rowRadio = row.querySelector(SELECTORS.radio);
        if (!rowRadio) return;
        selectPurchaseType(rowRadio.value);
      });
    });

    subscriptionExtras?.addEventListener('click', () => {
      selectPurchaseType(PURCHASE_TYPE.subscription);
    });

    const theme = window.theme;
    if (theme?.PUB_SUB_EVENTS && typeof subscribe === 'function') {
      subscribe(theme.PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = event?.data?.variant?.id;
        if (!variantId) return;

        const eventSectionId = event.data.section_id || event.data.sectionId;
        if (eventSectionId && toStringId(eventSectionId) !== toStringId(sectionId)) return;
        updateUiForVariant(variantId);
      });
    }

    const variantIdInput = getVariantIdInput(container, sellingPlanInput);
    if (variantIdInput) {
      variantIdInput.addEventListener('change', (event) => {
        updateUiForVariant(event.target.value);
      });
      updateUiForVariant(variantIdInput.value);
      return;
    }

    applyCommonUiState();
  };

  const init = () => {
    const containers = document.querySelectorAll(SELECTORS.container);
    containers.forEach(initContainer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();